export function deepComparison<T>(first: T, second: T) {
  /* Checking if the types and values of the two arguments are the same. */
  if (first === second) return true;

  /* Checking if any arguments are null */
  if (first === null || second === null) return false;

  /* Checking if any argument is none object */
  if (typeof first !== "object" || typeof second !== "object") return false;

  /* Using Object.getOwnPropertyNames() method to return the list of the objects’ properties */
  const firstKeys = Object.getOwnPropertyNames(first) as (keyof typeof first)[];

  const secondKeys = Object.getOwnPropertyNames(
    second,
  ) as (keyof typeof second)[];

  /* Checking if the objects' length are same*/
  if (firstKeys.length !== secondKeys.length) return false;

  /* Iterating through all the properties of the first object with the for of method*/
  for (const key of firstKeys) {
    /* Making sure that every property in the first object also exists in second object. */
    if (!Object.prototype.hasOwnProperty.call(second, key)) return false;

    /* Using the deepComparison function recursively (calling itself) and passing the values of each property into it to check if they are equal. */
    if (!deepComparison(first[key], second[key])) return false;
  }

  /* if no case matches, returning true */
  return true;
}

export type INestedObject = object;

export type IFlatObject = Record<string, string>;

/**
 * flat your object
 * @param input nested object or undefined
 * @returns object
 * 
 * @example
 * const stuff = {
    a1: {
      b1: {
        c1: 'value c1',
        c2: 'value c2',
        c3: {
          d1: 'value d1'
        }
      }
    }
  }
  flatten(stuff): 
  {
    c1: 'value c1',
    c2: 'value c2',
    d1: 'value d1'
  }
 * 
 */
export const flatten = (
  input: INestedObject | undefined,
): IFlatObject | undefined => {
  if (input === undefined) return undefined;
  const result: IFlatObject = {};
  const step = (obj: INestedObject | string, prefix: string) => {
    if (typeof obj !== "object") {
      result[prefix] = obj;
      return;
    }
    for (const key in obj) {
      step(obj[key as keyof typeof obj], key);
    }
  };

  step(input, "");
  return result;
};
